/**
 * React Error Boundary Components
 *
 * Provides simple error boundaries for different parts of the application
 */

import { AlertTriangle, Home, RefreshCw } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'

/**
 * Error boundary state
 */
interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

/**
 * Error boundary props
 */
interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  showDetails?: boolean
  level?: 'page' | 'component' | 'feature'
}

/**
 * Base Error Boundary Component
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props

    console.error('Error caught by boundary:', error)
    console.error('Component stack:', errorInfo.componentStack)

    // Store error info in state
    this.setState({ errorInfo })

    // Call custom error handler
    onError?.(error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    const { hasError, error, errorInfo } = this.state
    const {
      children,
      fallback,
      showDetails = false,
      level = 'component',
    } = this.props

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback
      }

      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          level={level}
          showDetails={showDetails}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
        />
      )
    }

    return children
  }
}

/**
 * Error Fallback UI Component
 */
interface ErrorFallbackProps {
  error?: Error
  errorInfo?: ErrorInfo
  level: 'page' | 'component' | 'feature'
  showDetails: boolean
  onRetry: () => void
  onGoHome: () => void
}

function ErrorFallback({
  error,
  errorInfo,
  level,
  showDetails,
  onRetry,
  onGoHome,
}: ErrorFallbackProps) {
  const isPageLevel = level === 'page'
  const isComponentLevel = level === 'component'

  return (
    <div
      className={`flex items-center justify-center p-4 ${isPageLevel ? 'min-h-screen' : 'min-h-[200px]'}`}
    >
      <Card className='w-full max-w-2xl'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-destructive'>
            <AlertTriangle className='h-5 w-5' />
            {isPageLevel ? 'Something Went Wrong' : 'Unable to Load Section'}
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <p className='text-muted-foreground'>
            {isPageLevel
              ? 'Something went wrong. Please try refreshing the page.'
              : 'This section encountered an error and cannot be displayed.'}
          </p>

          {/* Technical details only shown in development */}
          {showDetails && error && import.meta.env.DEV && (
            <div className='space-y-2'>
              <h4 className='font-medium text-sm'>
                Error Details (Development Only):
              </h4>
              <div className='bg-muted p-3 rounded-md text-sm font-mono'>
                <div className='text-destructive font-medium'>
                  {error.message}
                </div>
                {error.stack && (
                  <details className='mt-2'>
                    <summary className='cursor-pointer text-muted-foreground'>
                      Stack Trace
                    </summary>
                    <pre className='mt-2 text-xs overflow-auto max-h-32'>
                      {error.stack}
                    </pre>
                  </details>
                )}
                {errorInfo?.componentStack && (
                  <details className='mt-2'>
                    <summary className='cursor-pointer text-muted-foreground'>
                      Component Stack
                    </summary>
                    <pre className='mt-2 text-xs overflow-auto max-h-32'>
                      {errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          )}

          <div className='flex flex-wrap gap-2'>
            {isComponentLevel && (
              <Button onClick={onRetry} variant='default' size='sm'>
                <RefreshCw className='h-4 w-4 mr-2' />
                Try Again
              </Button>
            )}

            {isPageLevel && (
              <Button onClick={onGoHome} variant='default' size='sm'>
                <Home className='h-4 w-4 mr-2' />
                Go Home
              </Button>
            )}

            {isPageLevel && (
              <Button
                onClick={() => window.location.reload()}
                variant='outline'
                size='sm'
              >
                <RefreshCw className='h-4 w-4 mr-2' />
                Refresh Page
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Page-level Error Boundary
 */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary level='page' showDetails={false}>
      {children}
    </ErrorBoundary>
  )
}

/**
 * Feature-level Error Boundary
 */
export function FeatureErrorBoundary({
  children,
  featureName,
}: {
  children: ReactNode
  featureName: string
}) {
  return (
    <ErrorBoundary
      level='feature'
      showDetails={false}
      onError={error => {
        console.error(`Error in ${featureName} feature:`, error)
      }}
    >
      {children}
    </ErrorBoundary>
  )
}

/**
 * Component-level Error Boundary
 */
export function ComponentErrorBoundary({
  children,
  componentName,
}: {
  children: ReactNode
  componentName?: string
}) {
  return (
    <ErrorBoundary
      level='component'
      onError={error => {
        if (componentName) {
          console.error(`Error in ${componentName} component:`, error)
        }
      }}
    >
      {children}
    </ErrorBoundary>
  )
}
